import casesService from '../providers/casesService';
import gql from 'graphql-tag';

import get from 'lodash/get';
import isArray from 'lodash/isArray';
import toArray from 'lodash/toArray';
import mergeWith from 'lodash/mergeWith';

import effectChainQueries from '../queries/EffectChain';
import caseConfQueries from '../queries/CaseConfiguration';

import validateScenario from './scenario/validateScenario';
import validatePatientConditions from './patientConditions/validatePatientConditions';
import validateDialogue from './dialogue/validateDialogue';
import validateInterventions from './interventions/validateInterventions';
import validateIV from './iv/validateIV';
import validateIVOnAtStart from './iv/validateIVOnAtStart';
import validateMedications from './medications/validateMedications';
import validateMedsOnAtStart from './medications/validateMedsOnAtStart';
import validateFluids from './fluids/validateFluids';
import validateMonitors from './monitors/validateMonitors';
import validateCaseLogicDescription from './caseLogicDescription/validateCaseLogicDescription';
import validateEffectChains from './caseLogicDescription/validateEffectChains';
import validateTimers from './caseLogicDescription/validateTimers';
import validateNpcs from './npcs/validateNpcs';

async function validateCaseConf(caseConf) {
  const effectChains = await getEffectChainData(caseConf);

  const validationResults = [
    validateScenario(caseConf),
    validatePatientConditions(caseConf),
    validateDialogue(caseConf),
    validateNpcs(caseConf),
    validateInterventions(caseConf),
    validateIV(caseConf),
    validateIVOnAtStart(caseConf),
    validateMedications(caseConf),
    validateMedsOnAtStart(caseConf),
    validateFluids(caseConf),
    validateMonitors(caseConf),
    validateCaseLogicDescription(caseConf),
    validateEffectChains(caseConf, effectChains),
    validateTimers(caseConf, effectChains.filter(ec => !ec.error)),
  ];
  return mergeWith({}, ...validationResults, (objValue, srcValue) => {
    if (isArray(objValue)) {
      return objValue.concat(srcValue);
    }
    return undefined;
  });
}

async function validateCaseConfById(caseConfId) {
  const response = await casesService.query({
    query: gql(caseConfQueries.fetchById),
    variables: {
      id: caseConfId,
    },
    fetchPolicy: 'network-only',
  });
  const caseConf = response.data.caseConfigurations[0];
  return validateCaseConf(caseConf);
}

async function getEffectChainData(caseConf) {
  const nodes = toArray(get(caseConf, 'caseLogicDescription.nodes'));

  // Existing effect chains
  const existingEcNodes = nodes.filter(
    node => node.name === 'EffectChain' && get(node, 'data.effectChain.type') === 'EXISTING_EFFECT_CHAIN'
  );
  const ids = existingEcNodes.map(node => get(node, 'data.effectChain.id'));
  const existingffectChains = await fetchEffectChains(ids);

  // Custom effect chains
  const customEcNodes = nodes.filter(
    node => node.name === 'EffectChain' && get(node, 'data.effectChain.type') === 'CUSTOM_EFFECT_CHAIN'
  );
  const customEffectChains = customEcNodes.map(ecNode =>
    get(ecNode, 'data.effectChain')
  );

  // Medication effect chains
  const medicationsWithCustomEcs = (caseConf.dosageForms || []).filter(
    (caseConfMed) =>
      caseConfMed.customEffectChainId && caseConfMed.useDefaultEffectChain
  );
  const medicationCustomEcIds = medicationsWithCustomEcs.map(
    (caseConfMed) => caseConfMed.customEffectChainId
  );

  const medicationCustomEcs = (
    await fetchEffectChains(medicationCustomEcIds)
  ).map((effectChain, index) => ({
    ...effectChain,
    medication: medicationsWithCustomEcs[index],
  }));

  return [
    ...existingffectChains,
    ...customEffectChains,
    ...medicationCustomEcs,
  ];
}

async function fetchEffectChains(ids) {
  const responses = await Promise.all(
    ids.map(id => fetchEffectChain(id)),
  );
  return responses.map(response => {
    if (response.error) {
      return response;
    }
    const chains = get(response, 'data.effectChains');
    return (chains && chains.length > 0) ? chains[0] : undefined;
  });
}

function fetchEffectChain(id) {
  return casesService.query({
    query: gql(effectChainQueries.fetchById),
    variables: {
      id,
    },
    fetchPolicy: 'network-only',
  }).catch(() => {
    return {
      error: true,
      id,
    }
  });
}

export default validateCaseConf;

export { validateCaseConfById };
